import React, { useState, useEffect } from 'react';
import { DonorProfileService } from '../../services/DonorProfileService';
import { DonationService } from '../../services/DonationService';
import { useOrganization } from '../../contexts/OrganizationContext';
import * as WechatAuthService from '../../services/WechatAuthService';
import { showError, logError, logInfo } from '../../utils/errorHandler';
import { AuthLoginView } from '../AuthLoginView/AuthLoginView';
import { TopBar } from '../../components/TopBar/TopBar';
import { DonationStatsBar } from '../../components/DonationStatsBar/DonationStatsBar';
import { MyDonationIcon } from '../../components/Icons/MyDonationIcon';
import { MyInvoiceIcon } from '../../components/Icons/MyInvoiceIcon';
import { MyProfileIcon } from '../../components/Icons/MyProfileIcon';
import { MyCertificateIcon } from '../../components/Icons/MyCertificateIcon';
import projectIcon from '../../assets/icons/tab_project_normal.svg';
import { MineProfileBackground } from '../../components/MineProfileBackground/MineProfileBackground';
import './MineView.css';

/**
 * 我的页面视图（H5 用户端）
 */
interface MineViewProps {
  onAgreementClick: () => void;
  onMyInfoClick: () => void;
  onMyDonationsClick: () => void;
  onMyProjectsClick: () => void;
  onMyCertificatesClick: () => void;
  onMyTeamsClick: () => void;
}

export const MineView: React.FC<MineViewProps> = ({ onAgreementClick, onMyInfoClick, onMyDonationsClick, onMyProjectsClick, onMyCertificatesClick, onMyTeamsClick }) => {
  const { organization } = useOrganization();
  const [profile, setProfile] = useState({ name: '', phone: '', nickname: '', avatar: '' });
  const [loading, setLoading] = useState(true);
  const [showAuthLogin, setShowAuthLogin] = useState(false);
  const [donationStats, setDonationStats] = useState({ totalAmount: 0, projectCount: 0 });
  const [certificateCount, setCertificateCount] = useState(0);

  useEffect(() => {
    checkAuthAndLoadProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 监听 openId 变化，当授权完成后重新检查
  useEffect(() => {
    if (!showAuthLogin) {
      // 如果已经不在授权登录页面，不需要监听
      return;
    }

    const checkOpenId = () => {
      const openId = WechatAuthService.getOpenId();
      if (openId) {
        // 如果之前显示授权登录页面，现在有 openId 了，重新检查
        logInfo('[MineView] OpenId detected after auth, rechecking auth');
        checkAuthAndLoadProfile();
      }
    };

    // 立即检查一次
    checkOpenId();

    // 定期检查 openId（用于授权回调后的情况，最多检查10秒）
    let checkCount = 0;
    const maxChecks = 20; // 20次 * 500ms = 10秒
    const interval = setInterval(() => {
      checkCount++;
      if (checkCount > maxChecks) {
        clearInterval(interval);
        return;
      }
      checkOpenId();
    }, 500);
    
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAuthLogin]);

  const checkAuthAndLoadProfile = async () => {
    setLoading(true);
    try {
      const openId = WechatAuthService.getOpenId();
      if (!openId) {
        // 如果没有授权，显示授权登录页面
        // 保存当前tab状态，用于授权回调后恢复
        localStorage.setItem('pending_active_tab', 'mine');
        logInfo('[MineView] Saved active tab before auth', { tab: 'mine' });
        setShowAuthLogin(true);
        setLoading(false);
        return;
      }
      setShowAuthLogin(false);
      // 授权完成，清除保存的tab状态
      localStorage.removeItem('pending_active_tab');
      logInfo('[MineView] Cleared pending_active_tab after auth completion');
      await loadProfile();
    } catch (error) {
      logError('[MineView] Failed to check auth', error);
      setLoading(false);
    }
  };

  const loadProfile = async () => {
    setLoading(true);
    try {
      const openId = WechatAuthService.getOpenId();
      if (!openId) {
        setLoading(false);
        return;
      }

      const userProfile = await DonorProfileService.getProfile(openId);
      if (userProfile) {
        // 检查用户是否已注销
        if (userProfile.status === 'DEACTIVATED') {
          // 用户已注销，清除缓存，显示授权登录页面
          // 保存当前tab状态，用于授权回调后恢复
          localStorage.setItem('pending_active_tab', 'mine');
          logInfo('[MineView] Saved active tab before auth (deactivated user)', { tab: 'mine' });
          WechatAuthService.clearOpenId();
          WechatAuthService.clearWechatNickname();
          WechatAuthService.clearWechatAvatar();
          setShowAuthLogin(true);
          setLoading(false);
          return;
        }
        
        setProfile({
          name: userProfile.name || '',
          phone: userProfile.phone || '',
          nickname: userProfile.nickname || '',
          avatar: userProfile.avatar || '',
        });
      } else {
        // 如果没有用户信息，尝试从微信获取昵称和头像
        const nickname = WechatAuthService.getWechatNickname();
        const avatar = WechatAuthService.getWechatAvatar();
        setProfile({
          name: '',
          phone: '',
          nickname: nickname || '',
          avatar: avatar || '',
        });
      }

      // 加载捐赠统计
      await loadDonationStats(openId);
      // 加载证书数量
      await loadCertificateCount(openId);
    } catch (error) {
      logError('[MineView] Failed to load profile', error);
      showError('加载用户信息失败', 'MineView.loadProfile');
    } finally {
      setLoading(false);
    }
  };

  const loadCertificateCount = async (openId: string) => {
    try {
      // TODO: 调用后端接口获取用户证书数量
      // 暂时使用捐赠记录数作为证书数（每个已支付的捐赠都会生成证书）
      const params: any = {
        donorOpenId: openId,
        page: 1,
        limit: 1000,
      };
      // 根据机构类型决定使用哪个过滤参数
      if (organization?.type === 'PUBLIC') {
        params.organizationId = organization.id;
      } else if (organization?.type === 'EXECUTING') {
        params.executingOrganizationId = organization.id;
    }
      const response = await DonationService.getDonations(params);
      setCertificateCount(response.total || 0);
    } catch (error) {
      logError('[MineView] Failed to load certificate count', error);
      // 不显示错误，证书数量加载失败不影响页面显示
    }
  };

  const loadDonationStats = async (openId: string) => {
    try {
      // 获取用户在当前机构下的所有已支付捐赠记录
      if (!organization?.id) {
        logError('[MineView] Organization ID not available', null);
        return;
      }

      let totalAmount = 0;
      const projectIds = new Set<string>();
      let page = 1;
      const limit = 100;
      let hasMore = true;

      while (hasMore) {
        const params: any = {
          donorOpenId: openId,
          page,
          limit,
        };
        // 根据机构类型决定使用哪个过滤参数
        if (organization.type === 'PUBLIC') {
          params.organizationId = organization.id;
        } else if (organization.type === 'EXECUTING') {
          params.executingOrganizationId = organization.id;
        }
        const response = await DonationService.getDonations(params);

        // 如果当前页没有数据，说明已经到最后一页了
        if (response.list.length === 0) {
          hasMore = false;
          break;
        }

        response.list.forEach((donation) => {
          // 对于月捐，使用totalDonationAmount；对于单次捐，使用amount
          if (donation.donationType === 'MONTHLY' && donation.totalDonationAmount) {
            totalAmount += donation.totalDonationAmount;
          } else {
            totalAmount += donation.amount;
          }
          if (donation.projectId) {
            projectIds.add(donation.projectId);
          }
        });

        // 判断是否还有下一页：当前页小于总页数，且当前页返回的数据量等于 limit（说明可能还有下一页）
        hasMore = page < response.totalPages && response.list.length === limit;
        page++;
        
        // 防止无限循环
        if (page > 100) {
          break;
        }
      }

      setDonationStats({
        totalAmount,
        projectCount: projectIds.size,
      });
    } catch (error) {
      logError('[MineView] Failed to load donation stats', error);
      // 不显示错误，统计失败不影响页面显示
    }
  };

  if (loading) {
    return (
      <div className="mine-view">
        <div className="mine-loading">加载中...</div>
      </div>
    );
  }

  // 如果没有授权，显示授权登录页面
  if (showAuthLogin) {
    return (
      <div className="mine-view">
        <TopBar title="注册登录" showBack={false} />
        <AuthLoginView onAgreementClick={onAgreementClick} />
      </div>
    );
  }

  return (
    <div className="mine-view">
      {/* 背景从顶部开始铺，到 TopBar 最下面（TopBar 盖在上面） */}
      <MineProfileBackground />
      <TopBar title="我的" showBack={false} />
      <div className="mine-content">
        {/* 第一部分：用户头像、昵称和捐赠统计（半透明+模糊，透出背后色块） */}
        <div className="mine-profile-section">
          <div className="mine-profile-section-content">
          <div className="mine-profile-row">
          {profile.avatar && (
              <div 
                className="mine-profile-avatar" 
                onClick={onMyInfoClick}
                style={{ cursor: 'pointer' }}
              >
              <img src={profile.avatar} alt="头像" />
            </div>
          )}
            <div className="mine-profile-nickname">
              {profile.nickname || '用户昵称'}
            </div>
          </div>
          <DonationStatsBar
            totalAmount={donationStats.totalAmount}
            projectCount={donationStats.projectCount}
            onClickTotalAmount={onMyDonationsClick}
            onClickProjectCount={onMyProjectsClick}
          />
          </div>
        </div>

        {/* 第二部分：荣誉证书（始终显示入口，有数量时显示角标） */}
        <div className="mine-certificate-section" onClick={onMyCertificatesClick} style={{ cursor: 'pointer' }}>
          <div className="mine-certificate-content">
            <MyCertificateIcon className="mine-certificate-icon" />
            <div className="mine-certificate-text">
              <span>荣誉证书</span>
            </div>
          </div>
          <button type="button" className="mine-certificate-btn" onClick={(e) => { e.stopPropagation(); onMyCertificatesClick(); }}>立即查看</button>
        </div>

        {/* 第三部分：菜单列表 */}
        <div className="mine-section">
          <div className="mine-menu-item" onClick={onMyDonationsClick}>
            <MyDonationIcon className="mine-menu-icon" />
            <span>我的捐赠</span>
            <span className="arrow">›</span>
          </div>
          <div className="mine-menu-item" onClick={onMyProjectsClick}>
            <div className="mine-menu-icon mine-menu-icon-purple">
              <img src={projectIcon} alt="我的项目" />
            </div>
            <span>我的项目</span>
            <span className="arrow">›</span>
          </div>
          <div className="mine-menu-item" onClick={onMyTeamsClick}>
            <div className="mine-menu-icon mine-menu-icon-purple">👥</div>
            <span>我的一起捐</span>
            <span className="arrow">›</span>
          </div>
          <div className="mine-menu-item">
            <MyInvoiceIcon className="mine-menu-icon" />
            <span>捐赠发票</span>
            <span className="arrow">›</span>
          </div>
          <div className="mine-menu-item" onClick={onMyInfoClick}>
            <MyProfileIcon className="mine-menu-icon" />
            <span>我的信息</span>
            <span className="arrow">›</span>
          </div>
        </div>
      </div>
    </div>
  );
};
